import { TENANT_TYPE_NAMES } from "@closer/shared";
import { Avatar, useToast } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { ChevronDownIcon } from "../components/icons";
import { api, ApiRequestError } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { visibleProfileMenuItems } from "../lib/profileMenu";
import {
  INVITATION_INBOX_QUERY_KEY,
  type InvitationInbox,
} from "../pages/invitations/MyInvitationsPage";
import { TopBarPanel } from "./TopBarPanel";

/**
 * Üst Bar profil menüsü (CLOS-104 `[065]`).
 *
 * ### Hesap özeti nereden geliyor
 *
 * Ad ve e-posta OTURUM yükünden (`useAuth().user`). İkinci bir profil sorgusu açmak, aynı
 * gerçeğin iki kaynağı olurdu ve ikisi ayrıştığında menüde eski ad görünürdü.
 *
 * ⚠ **Profilim ekranı geldiğinde** (`[017]`'nin arayüzü) kaydetmeden sonra
 * `refreshSession()` çağırmak ZORUNLU — `login`/`register` zaten öyle yapıyor. Yoksa
 * kullanıcı adını değiştirir ve menüde eskisini görmeye devam eder.
 *
 * Rol AKTİF BAĞLAMDAN (`[043]`) okunuyor, üyelik listesinden değil: `can()` de aynı
 * kaynaktan besleniyor ve iki kaynak ayrışırsa ekranda yazan rol ile geçerli yetkiler
 * çelişirdi (`[060]` ile aynı karar).
 *
 * ### Kanonik liste (CLOS-843 ile revize)
 *
 * Profilim · Hesap ve Güvenlik · Aktif Oturumlar · Onaylarım · Davetlerim · Dil ve
 * Görünüm · Çıkış Yap. Kayıt `lib/profileMenu.ts`'te ve bekçisi orada.
 *
 * ⚠ Bu blok bir süre "menüde bugün BAĞLANTI YOK, kartın saydığı sekiz öğe" diyordu; oysa
 * Hesap ve Güvenlik, Davetlerim ve Dil ve Görünüm çoktan açılmıştı. Yorum ürünle birlikte
 * güncellenmeyince kanonik liste referansı yanlış bir gerçeği anlatır oldu — bir sonraki
 * okuyan buna göre karar verirdi.
 *
 * Hedefi olmayan öğe hâlâ ÇİZİLMİYOR (ADR-0014): Profilim ve Aktif Oturumlar `route: null`
 * ve ekranları geldiğinde tek satır değişecek.
 *
 * ### Yetki menüye öğe EKLEYEMEZ
 *
 * `visibleProfileMenuItems` bir permission parametresi almıyor. Kartın kriteri
 * *"Organizasyon Yönetimi, Abonelik ve Müvekkil Alanı yönetimi Profil menüsüne
 * taşınmaz"* böylece yapısal olarak garanti — bir bayrak eklenseydi kural bir gün
 * gevşetilebilirdi.
 */
export function ProfileMenu() {
  const { user, activeTenant, activeContext, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [cikiliyor, setCikiliyor] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const ogeler = visibleProfileMenuItems();

  /**
   * Bekleyen davet rozeti (CLOS-227 `[146]`).
   *
   * ⚠ Tutarlılık MEKANİZMADAN geliyor, sözden değil: sayıyı SUNUCU hesaplıyor ve rozet,
   * Davetlerim merkeziyle AYNI sorgu anahtarını okuyor. Menü kendi sayımını yapsaydı ya
   * da ayrı bir uçtan beslenseydi iki yüzey bir gün ayrışır ve kartın *"menüdeki sayı
   * merkezle tutarlıdır"* kriteri sessizce bozulurdu. Merkezdeki kabul/ret aynı anahtarı
   * geçersiz kılıyor; rozet kendiliğinden düşüyor.
   */
  const { data: davetKutusu } = useQuery({
    queryKey: INVITATION_INBOX_QUERY_KEY,
    queryFn: async () => (await api<InvitationInbox>("/api/invitations/inbox")).data,
    enabled: Boolean(user),
  });
  const bekleyenDavet = davetKutusu?.pendingCount ?? 0;

  /**
   * ⚠ SIRA: önce sunucu oturumu, sonra istemci bağlamı. Kartın çalışma zamanı koşulu bunu
   * şart koşuyor ve `logout()` (`[015]`) zaten bu sırayı uyguluyor.
   *
   * Sunucu çağrısı düşerse istemci bağlamı TEMİZLENMİYOR: temizleseydik kullanıcı
   * "çıktım" sanır ama sunucudaki oturumu yaşamaya devam ederdi — paylaşılan bir
   * bilgisayarda bu tam olarak güvenlik açığının kendisi.
   */
  async function cikisYap() {
    setCikiliyor(true);
    try {
      await logout();
      setOpen(false);
      navigate("/login");
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "Çıkış yapılamadı. Lütfen tekrar deneyin.",
      );
    } finally {
      setCikiliyor(false);
    }
  }

  const tetikleyici = (
    <button
      type="button"
      onClick={() => setOpen((onceki) => !onceki)}
      aria-expanded={open}
      /**
       * Erişilebilir ad SABİT. Kullanıcının adına bağlansaydı dar ekranda (ad gizliyken)
       * düğmenin adı boşalır ve ekran okuyucu yalnız "düğme" derdi.
       */
      aria-label="Hesap menüsü"
      className="flex h-11 items-center gap-2.5 rounded-xl border border-border-default bg-surface px-2 pr-2.5 transition-colors hover:border-border-strong hover:bg-canvas focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary lg:pl-2.5"
    >
      <Avatar name={user?.name ?? ""} size="md" />
      {/* Ad dar ekranda gizleniyor; erişilebilir ad yukarıdaki sabit etiketten geliyor. */}
      {/**
       * ⚠ Çipte YALNIZ AD. Onaylı tasarımda adın altında bir de rol satırı var ama rolün
       * KANONİK yeri panelin hesap özeti (`[043]` bağlamı, `[065]` kartı) ve bir test
       * onu tek bir düğüm olarak arıyor. Rolü çipe de yazmak aynı gerçeği ekranda iki
       * kere gösterip o ölçümü ikircikli hâle getirirdi; kullanıcı için de kazanç yok —
       * rol menü açılır açılmaz zaten görünüyor.
       */}
      <span className="hidden max-w-36 truncate text-sm font-medium text-fg-heading lg:inline">
        {user?.name}
      </span>
      <ChevronDownIcon className="hidden size-4 shrink-0 text-fg-faint lg:block" />
    </button>
  );

  return (
    <TopBarPanel
      open={open}
      onClose={() => setOpen(false)}
      title="Hesap menüsü"
      trigger={tetikleyici}
    >
      <div className="w-72 max-w-full">
        <div
          data-testid="hesap-ozeti"
          className="flex items-center gap-3 border-b border-border-subtle px-4 py-3.5"
        >
          <Avatar name={user?.name ?? ""} size="lg" label="Hesap avatarı" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-fg-heading">{user?.name}</p>
            <p className="truncate text-xs text-fg-soft">{user?.email}</p>
          </div>
        </div>

        {activeTenant ? (
          <div className="border-b border-border-subtle px-4 py-2.5">
            <p className="truncate text-xs text-fg-soft">
              {activeTenant.name} · {TENANT_TYPE_NAMES[activeTenant.type]}
            </p>
            {/* Rol yalnız bağlam YÜKLENDİYSE: yanlış rol göstermektense hiç göstermemek doğru. */}
            {activeContext ? (
              <p className="truncate text-xs text-fg-soft">
                {activeContext.roleName ?? "Bu alanda rol atanmaz"}
              </p>
            ) : null}
          </div>
        ) : null}

        {/*
          ⚠ `role="menu"` / `role="menuitem"` BİLEREK YOK.

          Vardı ve tutulmayan bir sözdü: `role="menu"` ekran okuyucuyu odak kipine sokar,
          kullanıcı ok tuşuna basar ve hiçbir şey olmaz — ok tuşu, Home/End, roving
          tabindex hiçbiri yazılmamıştı. Rol yokken kullanıcı düğme listesi bekler ve
          Tab'la gezer; o beklenti karşılanıyor.

          ⚠ Burada rol AYRICA YANLIŞTI: `role="menuitem"` bir KOMUT vaadi, oysa bu
          listenin çoğu öğesi GEZİNME (Profilim, Hesap ve Güvenlik, Onaylarım, Davetlerim
          — hepsi `navigate` çağırıyor). Yalnız "Çıkış Yap" gerçek komut. Ok tuşu
          eklenseydi bile rol yanlış kalırdı.

          Kural `packages/ui/src/Tabs.tsx`'te yazılı: *"yardımcı teknolojiye tutulmayacak
          bir söz verme"* — orada `role="tablist"` aynı gerekçeyle reddedilmiş.

          ⚠ `aria-haspopup` da kalktı: `TopBarPanel` masaüstünde `Popover`, mobilde
          `Drawer` (`role="dialog"`) çiziyor; tek bir değer ikisini birden doğru
          anlatamıyor. `aria-expanded` yerinde ve doğru.
        */}
        <ul aria-label="Hesap menüsü" className="py-1">
          {ogeler.map((oge) => (
            <li key={oge.key}>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(oge.route!);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-fg-strong transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
              >
                <span>{oge.label}</span>
                {/* Sıfır rozet ÇİZİLMİYOR: "0" bir bildirim değil, gürültüdür. */}
                {oge.key === "invitations" && bekleyenDavet > 0 ? (
                  <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-on-inverse">
                    {bekleyenDavet}
                  </span>
                ) : null}
              </button>
            </li>
          ))}

          {/* Çıkış bir bağlantı değil KOMUT: kayıtta yok, menünün en altında ayrı duruyor. */}
          <li>
            <button
              type="button"
              onClick={() => void cikisYap()}
              disabled={cikiliyor}
              className="block w-full border-t border-border-subtle px-4 py-2.5 text-left text-sm text-fg-strong transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary disabled:opacity-60"
            >
              Çıkış Yap
            </button>
          </li>
        </ul>
      </div>
    </TopBarPanel>
  );
}
